import React, { useState, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { FileText, Save, RotateCcw, Info, Pencil, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { patchOfferLetterTemplate, previewTemplateClausesPdf } from '../api';

/**
 * Rich-text editor toolbar config (slim).
 */
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean'],
  ],
};

/**
 * Default text shown when an admin has not yet customised a clause.
 * MUST mirror the backend defaults in `offer_letters.py`.
 */
const CLAUSE_DEFAULTS = {
  // Page 1 overrides — mirror the defaults in backend/routers/offer_letters.py
  // in the `_p1_text(...)` calls. Placeholders in `{braces}` are substituted
  // per-candidate by the PDF generator.
  page1_salutation:
    'Dear <b>Mr./Ms. {candidate_name}</b>,',
  page1_intro_paragraph:
    'With reference to your application and subsequent discussions, we are pleased to offer you the position of <b>{designation}</b> at {company_name}, deployed at our client location in <b>{work_location}</b> on a fixed-term contractual basis. Kindly complete all joining formalities within <b>{joining_days} working days</b> from the date of joining. Your employment will commence from <b>{start_date}</b>.',
  page1_terms_of_engagement_heading: '<b>Terms of Engagement:</b>',
  page1_terms_of_engagement_body:
    'Your initial assignment shall commence from {start_date}, subject to the terms and conditions outlined in the Letter of Engagement annexed herewith.',
  page1_compensation_preamble:
    'Your Total Cost to Company will be <b>Rs. {total_ctc} PA</b> (including Variable):',
  tds_clause:
    'Income Tax (TDS) will be deducted at source from the salary as per the provisions of the Income Tax Act, 1961, based on the applicable tax regime and the investment declarations submitted by the employee.',
  attendance_policy:
    'Your attendance shall be determined based on the successful completion and documentation of client presentations in the designated CRM system:',
  attendance_performance_compliance:
    "Attendance and working hours are governed by the Company's Attendance Policy as detailed in <b>Annexure A</b>, which will be shared after joining. Employees are required to adhere to the defined attendance requirements, reporting schedules, and working hours.<br/><br/>In addition, employees are subject to the <b>Daily Attendance Criteria</b> set out in Annexure A. Performance expectations, productivity benchmarks, and daily KRAs will be measured and evaluated in accordance with those provisions.<br/><br/>Any incentive payments, attendance‑related benefits or deductions, disciplinary actions, and performance evaluations will be determined based on compliance with the Attendance Policy and the Daily Attendance Criteria, as amended by the Company from time to time. The Company reserves the right to modify, revise, or update these policies at its sole discretion; such revisions will be binding on employees from the effective date of communication.<br/><br/>By accepting the Annexure, the employee acknowledges that they have read, understood, and agreed to comply with the Attendance Policy and the Daily Attendance Criteria and any subsequent amendments thereto.",
  dress_code_policy:
    "All employees are required to adhere to the prescribed dress code standards. The uniform cost shall be recovered from your first month's remuneration.",
  separation_policy_intro:
    "Either party may terminate the employment relationship by providing thirty (30) days' written notice or payment in lieu thereof.",
  separation_policy_bullets:
    '<ul><li><b>Unsatisfactory Performance:</b> The Company may initiate a Performance Improvement Plan (PIP) for 7 days. Failure to improve may result in immediate termination.</li><li><b>Absence:</b> \'No Call No Show\' for 3 consecutive days shall be grounds for immediate termination. Unauthorized absence exceeding 3 working days may result in termination.</li><li><b>Policy Violations:</b> Any violation of Company policies, Code of Conduct, NDA, or contractual obligations shall constitute grounds for disciplinary action, including termination.</li></ul>',
};

/**
 * Catalogue of editable clauses, grouped into dialog tabs.
 */
const CLAUSE_CATALOG = [
  {
    category: 'page1',
    label: 'Page 1',
    clauses: [
      {
        key: 'page1_salutation',
        title: 'Salutation',
        help: 'Rendered right below the reference/date block. Placeholders: {candidate_name}. Default: "Dear Mr./Ms. {candidate_name},".',
        type: 'rich',
      },
      {
        key: 'page1_intro_paragraph',
        title: 'Opening paragraph',
        help: 'The "With reference to your application…" block. Placeholders: {candidate_name}, {designation}, {work_location}, {company_name}, {start_date}, {joining_days}. Leave blank to keep the default copy.',
        type: 'rich',
      },
      {
        key: 'page1_terms_of_engagement_heading',
        title: 'Terms of Engagement — heading',
        help: 'Bold heading immediately after the opening paragraph. Default: "Terms of Engagement:".',
        type: 'rich',
      },
      {
        key: 'page1_terms_of_engagement_body',
        title: 'Terms of Engagement — body',
        help: 'Body paragraph under the heading. Placeholders: {start_date}. Default references the annexed Letter of Engagement.',
        type: 'rich',
      },
      {
        key: 'page1_compensation_preamble',
        title: 'Compensation preamble',
        help: 'One-line summary shown just above the salary breakdown table. Placeholders: {total_ctc}. Default: "Your Total Cost to Company will be Rs. {total_ctc} PA (including Variable):".',
        type: 'rich',
      },
      {
        key: 'tds_clause',
        title: 'TDS clause (note under salary table)',
        help: 'Italic note rendered directly under the compensation table in BOTH the offer letter and the salary annexure. Placeholders: {candidate_name}, {company_name}, {total_ctc}. Reset to default or clear the text to suppress the note.',
        type: 'rich',
      },
    ],
  },
  {
    category: 'attendance',
    label: 'Attendance',
    clauses: [
      {
        key: 'attendance_policy',
        title: 'Attendance Policy (intro paragraph)',
        help: 'Shown above the CRM attendance table for DC Intern / Digital Consultant / Team Lead templates. NOT shown for GoNukkad — see "Attendance and Performance Compliance" instead.',
        type: 'rich',
      },
      {
        key: 'attendance_performance_compliance',
        title: 'Attendance and Performance Compliance (GoNukkad)',
        help: 'Replaces the CRM attendance table for the GoNukkad Sales - DC template.',
        type: 'rich',
      },
    ],
  },
  {
    category: 'legal',
    label: 'Legal Terms',
    clauses: [
      {
        key: 'legal_confidentiality',
        title: 'Confidentiality & Non-Disclosure',
        help: 'The first clause of the legal-terms page. Placeholders: {candidate_name}, {company_name}. Blank = use built-in default.',
        type: 'rich',
      },
      {
        key: 'legal_non_compete',
        title: 'Non-Compete',
        help: 'Blank = use built-in default. Placeholders: {candidate_name}, {company_name}, {work_location}.',
        type: 'rich',
      },
      {
        key: 'legal_non_solicit',
        title: 'Non-Solicitation',
        help: 'Restrictions on soliciting employees / clients post-employment. Blank = default.',
        type: 'rich',
      },
      {
        key: 'legal_ip_assignment',
        title: 'Intellectual Property Assignment',
        help: 'IP created during employment vests with the company. Blank = default.',
        type: 'rich',
      },
      {
        key: 'legal_arbitration',
        title: 'Dispute Resolution / Arbitration',
        help: 'Seat, arbitrator selection, language. Blank = default.',
        type: 'rich',
      },
      {
        key: 'legal_governing_law',
        title: 'Governing Law & Jurisdiction',
        help: 'Applicable law and courts. Blank = default.',
        type: 'rich',
      },
      {
        key: 'legal_termination',
        title: 'Termination, Notice Period & Recovery',
        help: 'Placeholders: {candidate_name}, {company_name}. Blank = default.',
        type: 'rich',
      },
      {
        key: 'legal_severability',
        title: 'Severability',
        help: 'Standard severability boilerplate. Blank = default.',
        type: 'rich',
      },
      {
        key: 'legal_survivability',
        title: 'Survival of Obligations',
        help: 'Standard survival boilerplate. Blank = default.',
        type: 'rich',
      },
    ],
  },
  {
    category: 'separation',
    label: 'Separation',
    clauses: [
      { key: 'separation_policy_intro', title: 'Separation Policy (intro paragraph)', type: 'rich' },
      { key: 'separation_policy_bullets', title: 'Separation Policy (bullet list)', help: 'Use bullet list formatting.', type: 'rich' },
    ],
  },
  {
    category: 'dress_code',
    label: 'Dress Code',
    clauses: [
      { key: 'dress_code_policy', title: 'Dress Code Policy', type: 'rich' },
    ],
  },
  {
    category: 'terms',
    label: 'Probation & Notice',
    clauses: [
      { key: 'probation_period', title: 'Probation Period', source: 'terms', type: 'text', help: 'Short text shown in the PDF (e.g. "3 months", "6 months").' },
      { key: 'notice_period', title: 'Notice Period', source: 'terms', type: 'text' },
      { key: 'working_hours', title: 'Working Hours', source: 'terms', type: 'text' },
      { key: 'confidentiality_clause', title: 'Confidentiality Clause', source: 'terms', type: 'text' },
    ],
  },
];

/**
 * Normalise HTML for equality comparison.
 *  - decode common entities (&nbsp; &amp; &lt; &gt; &#39;)
 *  - normalise unicode dashes / smart quotes to ASCII counterparts
 *  - strip all tags & collapse whitespace
 * We compare the PLAIN-TEXT version so cosmetic Quill rewrites
 * (`<p>x</p>` vs `<b>x</b>` lifted to <strong>) don't mask real edits
 * and the default text won't be misclassified as a "Custom" override
 * just because Quill normalised entities or characters.
 */
const normalisePlain = (html) => {
  if (html === undefined || html === null) return '';
  return String(html)
    // Decode the entities Quill is most likely to introduce
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    // Strip every HTML tag
    .replace(/<[^>]+>/g, ' ')
    // Normalise unicode punctuation that Quill or browsers may rewrite
    .replace(/[\u2010-\u2015\u2212]/g, '-')   // non-breaking hyphens, en/em dashes, minus sign
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/\u00A0/g, ' ')                   // non-breaking space
    // Collapse all whitespace
    .replace(/\s+/g, ' ')
    .trim();
};

const isMeaningful = (html) => normalisePlain(html).length > 0;

/** True when `html` is a real override (non-empty AND distinct from the default). */
const isOverrideAgainst = (html, defaultHtml) => {
  if (!isMeaningful(html)) return false;
  return normalisePlain(html) !== normalisePlain(defaultHtml);
};

/**
 * Single rich-clause editor row.
 *
 * UX design — fixes earlier "false Custom badge" bug:
 *   - When NOT customised: show the read-only default text + a "Customise"
 *     button. Quill is not mounted, so it can't emit auto-onChange.
 *   - When customised: show Quill bound directly to state (no `||` fallback)
 *     so user keystrokes are the only source of state mutation.
 *   - "Reset to default" deletes the key from state → returns to the read-only
 *     view, so the badge flips back instantly.
 */
const RichClauseRow = ({ clause, value, onChange, onReset }) => {
  const defaultHtml = CLAUSE_DEFAULTS[clause.key] || '';
  const isOverride = isOverrideAgainst(value, defaultHtml);
  // `editing` lets the admin enter edit mode even before they've typed —
  // we don't auto-promote a default into "Custom" state.
  const [editing, setEditing] = useState(false);

  // When the parent reloads a new template, collapse the editor.
  useEffect(() => {
    setEditing(false);
  }, [clause.key, defaultHtml]);

  const showEditor = editing || isOverride;

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <Label className="text-sm font-medium text-slate-800 flex items-center gap-2">
            {clause.title}
            {isOverride ? (
              <Badge className="bg-amber-100 text-amber-800" data-testid={`badge-custom-${clause.key}`}>Custom</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500" data-testid={`badge-default-${clause.key}`}>Default</Badge>
            )}
          </Label>
          {clause.help && (
            <p className="text-xs text-slate-500 flex items-start gap-1 mt-1">
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              <span>{clause.help}</span>
            </p>
          )}
        </div>
        {isOverride ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onReset(); setEditing(false); }}
            data-testid={`reset-${clause.key}`}
          >
            <RotateCcw size={14} className="mr-1" />
            Reset to default
          </Button>
        ) : !editing ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
            data-testid={`customise-${clause.key}`}
          >
            <Pencil size={14} className="mr-1" />
            Customise
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(false)}
            data-testid={`cancel-edit-${clause.key}`}
          >
            Cancel
          </Button>
        )}
      </div>

      {showEditor ? (
        <div className="bg-white rounded" data-testid={`clause-editor-${clause.key}`}>
          <ReactQuill
            theme="snow"
            // Bind strictly to state. Initial value = current override OR the
            // default text (so admins can edit on top of the default).
            value={value || defaultHtml}
            onChange={onChange}
            modules={quillModules}
          />
        </div>
      ) : (
        <div
          className="text-sm text-slate-600 bg-slate-50 rounded p-3 border border-dashed border-slate-200 max-h-40 overflow-y-auto"
          data-testid={`clause-default-preview-${clause.key}`}
          dangerouslySetInnerHTML={{ __html: defaultHtml }}
        />
      )}
    </div>
  );
};

const TemplateClauseEditor = ({ template, open, onOpenChange, onSaved }) => {
  // Working copies — initially mirror what came from the server, then mutated
  // only by explicit user actions.
  const [clauses, setClauses] = useState({});
  const [terms, setTerms] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [activeTab, setActiveTab] = useState('attendance');

  // Sync editor state whenever the parent opens a (possibly different) template.
  useEffect(() => {
    if (open && template) {
      setClauses({ ...(template.clauses || {}) });
      setTerms({ ...(template.terms_and_conditions || {}) });
      setActiveTab('attendance');
    }
  }, [open, template]);

  const customisedCount = useMemo(() => {
    let n = 0;
    for (const grp of CLAUSE_CATALOG) {
      for (const c of grp.clauses) {
        if (c.source === 'terms') {
          if ((terms[c.key] ?? '').trim()) n += 1;
        } else if (isOverrideAgainst(clauses[c.key], CLAUSE_DEFAULTS[c.key] || '')) {
          n += 1;
        }
      }
    }
    return n;
  }, [clauses, terms]);

  const handleClauseChange = (key, html) => {
    // If Quill normalised the default text into a slightly different HTML
    // (entity encoding, smart-quote rewriting, etc.), treat it as a no-op
    // override so the badge stays "Default" until the user actually types.
    setClauses((prev) => {
      const defaultPlain = normalisePlain(CLAUSE_DEFAULTS[key] || '');
      if (normalisePlain(html) === defaultPlain) {
        if (prev[key] === undefined) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: html };
    });
  };

  const handleResetClause = (key) => {
    setClauses((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    toast.success('Reset to default — click Save Clauses to persist.');
  };

  const handleTermChange = (key, value) => {
    setTerms((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Build the cleaned payload used by both Save and Preview — keeps the
   * two flows consistent (you preview EXACTLY what would be saved).
   */
  const buildPayload = () => {
    const cleanedClauses = {};
    for (const [k, v] of Object.entries(clauses)) {
      if (isOverrideAgainst(v, CLAUSE_DEFAULTS[k] || '')) {
        cleanedClauses[k] = v;
      }
    }
    return {
      clauses: cleanedClauses,
      terms_and_conditions: {
        ...(template?.terms_and_conditions || {}),
        ...terms,
      },
    };
  };

  /**
   * Generate a preview PDF using the CURRENT (un-saved) draft and open it
   * in a new tab. Backend builds a placeholder offer letter, runs it through
   * the PDF pipeline, and cleans up after itself — nothing is persisted.
   */
  const handlePreview = async () => {
    if (!template?.id) return;
    setPreviewing(true);
    try {
      const payload = buildPayload();
      const res = await previewTemplateClausesPdf(template.id, payload);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      // Try to open in a new tab; fall back to download if popup blocked.
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `clause-preview-${template.name || 'template'}.pdf`;
        a.click();
      }
      // Revoke later — keep the URL alive long enough for the new tab to load.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Preview generated — opened in a new tab.');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to generate preview PDF');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!template?.id) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      await patchOfferLetterTemplate(template.id, payload);
      toast.success('Clauses saved — new offer letters will use the updated text.');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save clauses');
    } finally {
      setSaving(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="clause-editor-dialog"
        aria-describedby="clause-editor-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={20} className="text-indigo-600" />
            Edit Clauses — {template.name}
            <Badge variant="outline" className="ml-auto" data-testid="customised-count">
              {customisedCount} customised
            </Badge>
          </DialogTitle>
          <DialogDescription id="clause-editor-description" className="text-sm text-slate-500">
            Edits apply to <b>future</b> offer letters generated from this template. Existing offer
            letters keep the clauses they were issued with (frozen via template snapshot).
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="grid grid-cols-4 w-full" data-testid="clause-tabs">
            {CLAUSE_CATALOG.map((grp) => (
              <TabsTrigger key={grp.category} value={grp.category} data-testid={`clause-tab-${grp.category}`}>
                {grp.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {CLAUSE_CATALOG.map((grp) => (
            <TabsContent key={grp.category} value={grp.category} className="space-y-5 pt-4">
              {grp.clauses.map((c) => {
                if (c.source === 'terms') {
                  return (
                    <div key={c.key} className="border border-slate-200 rounded-lg p-4 bg-white">
                      <Label className="text-sm font-medium text-slate-800 mb-2 block">{c.title}</Label>
                      {c.help && (
                        <p className="text-xs text-slate-500 flex items-start gap-1 mb-2">
                          <Info size={12} className="mt-0.5 flex-shrink-0" />
                          <span>{c.help}</span>
                        </p>
                      )}
                      <Input
                        value={terms[c.key] ?? ''}
                        onChange={(e) => handleTermChange(c.key, e.target.value)}
                        placeholder={c.placeholder || ''}
                        data-testid={`clause-input-${c.key}`}
                      />
                    </div>
                  );
                }
                return (
                  <RichClauseRow
                    key={c.key}
                    clause={c}
                    value={clauses[c.key]}
                    onChange={(html) => handleClauseChange(c.key, html)}
                    onReset={() => handleResetClause(c.key)}
                  />
                );
              })}
            </TabsContent>
          ))}
        </Tabs>

        <DialogFooter className="mt-4 gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || previewing}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handlePreview}
            disabled={saving || previewing}
            data-testid="preview-pdf-btn"
          >
            {previewing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />}
            {previewing ? 'Generating…' : 'Preview PDF'}
          </Button>
          <Button onClick={handleSave} disabled={saving || previewing} data-testid="save-clauses-btn">
            <Save size={14} className="mr-1" />
            {saving ? 'Saving…' : 'Save Clauses'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateClauseEditor;
